<template>
  <div class="flex flex-col gap-4 sm:justify-start h-full">
    <div v-if="selectedRoutine" class="bg-gray-900 rounded-lg p-4 flex flex-col gap-2 flex-grow items-start">
      <div class="flex gap-2 w-full justify-between">
        <div
          class="w-16 h-16 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center mb-2"
        >
          <img src="../../assets/icons/routines.svg" class="w-8 h-8" />
        </div>

        <router-link
          :to="`/coach/${timeToLengthMap[selectedRoutine.duration_in_minutes].toLowerCase()}`"
          class="relative cursor-pointer inline-flex items-center justify-center h-8 w-8 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 bg-gray-800 hover:bg-gray-700"
        >
          <img
            src="../../assets/icons/rightarrow.svg"
            class="w-4 h-4 text-text-primary"
          />
        </router-link>
      </div>

      <div
        v-if="selectedRoutine?.completed"
        class="flex gap-1 py-1 px-2 rounded-lg bg-gray-800 items-center justify-start"
      >
        <div class="w-3 h-3 rounded-full border border-gray-700 bg-success-500" />
        <h2 class="text-text-secondary text-xs font-light">{{ $t('coach.routine.completed_today') }}</h2>
      </div>
      <div
        v-else
        class="flex gap-1 py-1 px-2 rounded-lg bg-gray-800 items-center justify-start"
      >
        <div class="w-3 h-3 rounded-full border border-gray-700" />
        <h2 class="text-text-secondary text-xs font-light">{{ $t('coach.routine.not_completed_today') }}</h2>
      </div>

      <div class="flex flex-col gap-1 pb-3 flex-1">
        <h2 class="text-text-primary font-medium text-sm">
          {{ selectedRoutine?.name }}
        </h2>
        <p
          class="text-text-secondary text-xs font-light flex-1 whitespace-pre-wrap"
          v-html="selectedRoutine?.description"
        />

      </div>

      <!-- Settings and actions for all users -->
      <div class="flex gap-2 w-full">
        <button
          id="launchCoachRoutine"
          @click="launchRoutine(selectedRoutine)"
          class="mt-auto py-2 flex-1 rounded-lg text-text-primary text-xs font-medium duration-300 bg-primary-600 hover:bg-primary-500 flex items-center justify-center gap-2"
        >
          {{ $t('coach.routine.launch') }}
        </button>

        <RoutineSettingsDropdown
          :current-length="selectedLength"
          :current-random-maps="selectedRandomMaps"
          :length-options="routineLengthOptions"
          :is-refreshing="isRefreshing"
          :show-success-flash="showSuccessFlash"
          :updated-at="selectedRoutine?.updated_at"
          @update:length="handleLengthUpdate"
          @update:random-maps="handleRandomMapsUpdate"
          @refresh-routine="refreshRoutine"
        />
      </div>
    </div>

    <div v-else class="bg-gray-900 rounded-lg p-6 flex flex-col gap-4 flex-grow">
      <h1 class="text-lg font-medium text-text-primary flex gap-2 flex-1">
        {{ $t('coach.routine.title') }}
      </h1>

      <p class="text-text-secondary text-sm font-light">{{ $t('coach.routine.none_found') }}</p>
    </div>
  </div>
</template>

<script setup lang="js">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { useStore } from 'vuex';
import { lengthToTimeMap, timeToLengthMap } from '../../utils/coach_routines';
import RoutineSettingsDropdown from '../RoutineSettingsDropdown.vue';

const store = useStore();
const { t } = useI18n();

const props = defineProps({
  routines: {
    type: Array,
    required: true,
  },
  user: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['launchRoutine', 'refreshRoutines']);

const routineLengths = computed(() => props.routines.sort((a, b) => a.duration_in_minutes - b.duration_in_minutes).map((routine) => timeToLengthMap[routine.duration_in_minutes]));

// Create options for the settings dropdown
const routineLengthOptions = computed(() => routineLengths.value.map((length) => ({
  value: length,
  label: t('coach.routine.length_option', { length, minutes: getTimeLength(length) }),
})));

const selectedLength = ref('Short');

// Refresh state
const isRefreshing = ref(false);
const showSuccessFlash = ref(false);
const selectedRandomMaps = ref(false);

function getTimeLength(length) {
  return lengthToTimeMap[length];
}

// Settings dropdown handlers
function handleLengthUpdate(length) {
  selectedLength.value = length;
}

function handleRandomMapsUpdate(value) {
  selectedRandomMaps.value = value;
}

// Refresh routine function
async function refreshRoutine() {
  if (isRefreshing.value) return;

  try {
    isRefreshing.value = true;

    const response = await axios.post('/task_lists/refresh_coach_routine', {
      random_maps: selectedRandomMaps.value,
    });

    if (response.status === 200) {
      // Emit event to parent to refresh routines data
      emit('refreshRoutines');

      // Show success flash effect
      showSuccessFlash.value = true;

      // Show success message
      store.commit('showMessage', {
        messageType: 'success',
        messageText: t('coach.routine.refreshed'),
      });

      // Hide flash effect and message after 2 seconds
      setTimeout(() => {
        showSuccessFlash.value = false;
        store.commit('hideMessage');
      }, 2000);
    }
  } catch (error) {
    console.error('Error refreshing routine:', error);

    // Show error message
    const errorMessage = error.response?.data?.message || error.response?.data?.error || t('coach.routine.refresh_failed');
    store.commit('showMessage', {
      messageType: 'error',
      messageText: errorMessage,
    });

    // Hide message after 5 seconds
    setTimeout(() => {
      store.commit('hideMessage');
    }, 5000);
  } finally {
    isRefreshing.value = false;
  }
}

function launchRoutine(routine) {
  emit('launchRoutine', { ...routine, selectedLength: selectedLength.value });
}

const selectedRoutine = computed(() => props.routines?.find((routine) => routine.duration_in_minutes === lengthToTimeMap[selectedLength.value]));

</script>
